/**
 * Skill motions (chosen per skill in render/skillFx.ts): a two-handed spear thrust, a spinning fan
 * sweep, a double palm push, a leaping downward strike, a sword-qi release, a meditative healing cast
 * and the rage of an ultimate. Each opens on a held wind-up key, so the move reads before it lands.
 */
import type { MotionDef, PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'spearDraw'
  | 'spearLunge'
  | 'fanOpen'
  | 'fanTurn'
  | 'fanSweep'
  | 'palmDraw'
  | 'palmPush'
  | 'leapCrouch'
  | 'leap'
  | 'slam'
  | 'qiGather'
  | 'qiRelease'
  | 'gatherUp'
  | 'gatherDown'
  | 'rageCoil'
  | 'rageRoar'
  | 'rageStrike';

export const POSES: PoseRigs<Poses> = {
  spearDraw: () => {
    // Spear drawn back to the hip in both hands, the weight rocked onto the back leg, the point
    // still on the target: the coil before a two-handed thrust.
    return {
      o: 4,
      lean: -2,
      tilt: -6,
      headDx: -1,
      f1: -10,
      f2: 4,
      arm: 'guard',
      far: 'fwd',
      face: 'block',
      sway: -0.5,
    };
  },
  spearLunge: () => {
    // Driven through: the longest bow stance, both hands on the shaft, the body behind the point.
    return {
      o: 7,
      lean: 4,
      tilt: 18,
      headDx: 1,
      f1: -11,
      f2: 10,
      arm: 'thrust',
      far: 'fwd',
      face: 'atk',
      sway: 2.5,
    };
  },
  fanOpen: () => {
    // The fan snapped open before the chest, turned away to wind the spin.
    return {
      o: 2,
      lean: -1,
      tilt: -6,
      headDx: -1,
      f1: -5,
      f2: 3,
      arm: 'chest',
      far: 'back',
      sway: -1,
    };
  },
  fanTurn: () => {
    // Mid-spin: up on the balls of the feet, the fan raised, the robe flaring out.
    return {
      o: 0,
      tilt: -4,
      f1: -3,
      f2: 4,
      l1: 1,
      hem: 3,
      flare: 3,
      sway: 2,
      arm: 'raise',
      far: 'up',
      face: 'cast',
    };
  },
  fanSweep: () => {
    // The sweep comes round: low, the open fan flung out level, the far hand thrown back.
    return {
      o: 4,
      lean: 2,
      tilt: 10,
      f1: -8,
      f2: 7,
      hem: 1,
      flare: 1,
      sway: 1.5,
      arm: 'out',
      far: 'back',
      face: 'atk',
    };
  },
  palmDraw: () => {
    // Both palms drawn back to the waist, the weight sunk on the back leg: the breath taken in.
    return {
      o: 4,
      lean: -2,
      tilt: -6,
      f1: -9,
      f2: 4,
      arm: 'hip',
      far: 'hip',
      face: 'block',
      sway: -0.5,
    };
  },
  palmPush: () => {
    // Both palms driven out together from the legs, the back leg straight, the body behind them.
    return {
      o: 6,
      lean: 4,
      tilt: 14,
      headDx: 1,
      f1: -10,
      f2: 9,
      arm: 'thrust',
      far: 'fwd',
      face: 'atk',
      sway: 2,
    };
  },
  leapCrouch: () => {
    // Sunk deep to spring, the blade (or fist) low behind.
    return {
      o: 7,
      lean: 1,
      tilt: 16,
      f1: -6,
      f2: 5,
      arm: 'back',
      far: 'fwd',
      face: 'block',
      sway: -1,
    };
  },
  leap: () => {
    // In the air above the foe: knees tucked, the blade raised high in both hands.
    return {
      o: 0,
      tilt: -8,
      f1: -4,
      f2: 3,
      l1: 7,
      l2: 9,
      hem: 4,
      flare: 2,
      sway: -1.5,
      arm: 'overhead',
      far: 'up',
      face: 'atk',
    };
  },
  slam: () => {
    // Landed on the blow: a deep crouch over the front knee, the blade driven down to the ground.
    return {
      o: 7,
      lean: 2,
      tilt: 18,
      headDx: 1,
      f1: -8,
      f2: 7,
      arm: 'low',
      far: 'back',
      face: 'atk',
      sway: 2,
    };
  },
  qiGather: () => {
    // The blade cocked back high behind the shoulder, the sword-finger levelled at the target.
    return {
      o: 4,
      lean: -1,
      tilt: -4,
      f1: -8,
      f2: 5,
      arm: 'windup',
      far: 'fwd',
      face: 'block',
      sway: -0.5,
    };
  },
  qiRelease: () => {
    // The blade flung out level, the qi leaving its edge; the far hand thrown back for balance.
    return {
      o: 3,
      lean: 3,
      tilt: 8,
      f1: -9,
      f2: 8,
      hem: 1,
      sway: 2,
      arm: 'out',
      far: 'back',
      face: 'atk',
    };
  },
  gatherUp: () => {
    // Drawing qi in: the arms opened wide and up, the chest open, the eyes shut (the weapon hand
    // kept out to the side, clear of the face).
    return { o: 1, tilt: -6, f1: -4, f2: 4, arm: 'out', far: 'up', face: 'cast', closed: true };
  },
  gatherDown: () => {
    // Sinking it to the dantian: a horse stance, the palms pressed together before the chest.
    return { o: 4, f1: -5, f2: 5, arm: 'chest', far: 'clasp', face: 'cast', closed: true };
  },
  rageCoil: () => {
    // Gathering rage: crouched low, head down, the fists (or blade) drawn in, shaking with it.
    return {
      o: 8,
      lean: -1,
      tilt: 20,
      headDx: 1,
      f1: -7,
      f2: 6,
      arm: 'low',
      far: 'back',
      closed: true,
    };
  },
  rageRoar: () => {
    // Bursting up with a roar: arched back, the weapon thrown high, the robe blown out.
    return {
      o: 0,
      lean: -1,
      tilt: -14,
      headDx: -1,
      f1: -8,
      f2: 7,
      hem: 3,
      flare: 3,
      sway: -1,
      arm: 'overhead',
      far: 'up',
      face: 'atk',
    };
  },
  rageStrike: () => {
    // The ultimate lands: the longest lunge, everything behind the blow.
    return {
      o: 7,
      lean: 4,
      tilt: 22,
      headDx: 1,
      f1: -11,
      f2: 10,
      hem: 1,
      sway: 2.5,
      arm: 'cut',
      far: 'back',
      face: 'atk',
    };
  },
};

export const MOTIONS = [
  // Skill motions (chosen per skill in render/skillFx.ts). Each opens on a held wind-up key, so the
  // move reads before it lands.
  /** Two-handed spear thrust: coiled back, driven through, recovered to the stance. */
  { id: 'spear', keys: ['spearDraw', 'spearLunge', 'ready'], ease: ['in', 'out'] },
  /** Spinning fan sweep: opened and wound, turned on the toes, swept round low. */
  { id: 'fan', keys: ['fanOpen', 'fanTurn', 'fanSweep'], ease: ['inOut', 'in'] },
  /** Double palm push: palms drawn to the waist, driven out together. */
  { id: 'palmPush', keys: ['palmDraw', 'palmPush', 'ready'], ease: ['in', 'out'] },
  /** Leaping downward strike: crouch, spring up with the blade high, come down on the foe. */
  { id: 'leap', keys: ['leapCrouch', 'leap', 'slam'], ease: ['out', 'in'] },
  /** Sword-qi release: the blade drawn low across the body, flung out level. */
  { id: 'qi', keys: ['qiGather', 'qiRelease', 'ready'], ease: ['in', 'out'] },
  /** Healing / meditative cast: qi drawn in with the arms raised, sunk to the dantian. */
  { id: 'heal', keys: ['ready', 'gatherUp', 'gatherDown'], ease: ['inOut', 'inOut'] },
  /** Ultimate: coiled with rage, bursting up, the great blow. */
  { id: 'ultimate', keys: ['rageCoil', 'rageRoar', 'rageStrike'], ease: ['out', 'in'] },
  // The gathering for each new way of charging a skill (see `CAST_MOTION` in renderer.ts).
  { id: 'castPalm', keys: ['ready', 'palmDraw'], ease: ['out'] },
  { id: 'castSpear', keys: ['ready', 'spearDraw'], ease: ['out'] },
  { id: 'castMeditate', keys: ['ready', 'gatherUp'], ease: ['inOut'] },
  { id: 'castRage', keys: ['ready', 'rageCoil'], ease: ['out'] },
  // Back into the stance after a skill motion that does not end in it.
  { id: 'recoverSlam', keys: ['slam', 'ready'], ease: ['inOut'] },
  { id: 'recoverFan', keys: ['fanSweep', 'ready'], ease: ['inOut'] },
  { id: 'recoverGather', keys: ['gatherDown', 'ready'], ease: ['inOut'] },
  { id: 'recoverRage', keys: ['rageStrike', 'ready'], ease: ['inOut'] },
] as const satisfies readonly MotionDef[];
